import React, {useState, useRef, MouseEvent, FC} from 'react';
import {Text, Input, Icon, PriorityIcon} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {Column} from '../../../../../../stores/Form';
import {FormDatePicker} from '../../../../../Form/components/Input/DateInput/DatePicker';
import {DataTestIds} from '../../../../constants';
import {texts} from '../../../../texts';
import {hasNodeIgnoreClassName} from '../../../../../../utils/clickOutsideDefaultHandler';
import styles from './AutoPlanningContent.module.scss';

export type AutoPlanningContentProps = {
    deliveryDateCol: Column;
    deliveryDateValue: string;
    setDeliveryDateValue: (value: string) => void;
    showExplanation?: boolean;
    inputRequired?: boolean;
};

const ignoreClassName = 'priority-datepicker';

export const AutoPlanningContent: FC<AutoPlanningContentProps> = ({
    deliveryDateCol,
    deliveryDateValue,
    setDeliveryDateValue,
    showExplanation = false,
    inputRequired = false
}) => {
    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>();
    const wrapperRef = useRef<HTMLDivElement>();

    const onClickOutside = (e: MouseEvent<HTMLElement>) => {
        const ignore = hasNodeIgnoreClassName(e.target as HTMLElement, ignoreClassName);
        if (ignore) {
            return;
        }
        setIsDatepickerOpen(false);
    };

    const clearDateValue = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onDateChange('');
        setIsDatepickerOpen(false);
    };

    const onDateChange = (date: string) => {
        setDeliveryDateValue(date);
    };

    return (
        <>
            {showExplanation && (
                <Text skin={'paragraph-2'} textColor={'secondary-500'}>
                    {texts.unassigned_automatically_explanation}
                </Text>
            )}
            <div
                className={cx(styles.datePickerInputWrapper, ignoreClassName)}
                onClick={() => setIsDatepickerOpen(true)}
                ref={wrapperRef}
            >
                <Input
                    value={deliveryDateValue}
                    label={texts.optimization_delivery_date}
                    required={inputRequired}
                    size={'large'}
                    componentClasses={styles.input}
                    readOnly
                    prefixIcon={
                        <div className={styles.datePickerTriggerWrapper}>
                            <FormDatePicker
                                value={deliveryDateValue}
                                columnType={deliveryDateCol.Type}
                                inputRef={inputRef}
                                isOpen={isDatepickerOpen}
                                onChange={selected => onDateChange(selected)}
                                setIsOpen={setIsDatepickerOpen}
                                portalEl={wrapperRef.current}
                                onClickOutside={onClickOutside}
                                position="fixed"
                                dataTestId={DataTestIds.datePicker}
                            />
                        </div>
                    }
                    dataTestId={DataTestIds.deliveryDateInput}
                >
                    <Icon
                        icon={PriorityIcon.closeSmall}
                        onClick={e => clearDateValue(e)}
                        dataTestId={DataTestIds.deliveryDateClearBtn}
                    />
                </Input>
            </div>
            <div ref={inputRef} className={styles.datePickerPosition}></div>
        </>
    );
};
